import React, { useEffect, useRef } from 'react';
import { AlertTriangle, Trash2, Info, HelpCircle, X } from 'lucide-react';

/**
 * Standardised confirm/alert dialog to replace window.confirm() and alert()
 *
 * Usage:
 *   <ConfirmDialog
 *     open={showConfirm}
 *     title="Delete Customer"
 *     message="This will permanently delete the customer and all associated jobs."
 *     variant="danger"           // 'danger' | 'warning' | 'info' (default)
 *     confirmLabel="Delete"      // default: "Confirm"
 *     cancelLabel="Cancel"       // default: "Cancel" — set to null for alert-style (no cancel)
 *     onConfirm={() => doThing()}
 *     onCancel={() => setShowConfirm(false)}
 *   />
 */
const ConfirmDialog = ({
  open,
  title = 'Confirm',
  message,
  variant = 'info',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel
}) => {
  const confirmRef = useRef(null);

  // Focus the confirm button when the dialog opens, and trap Escape
  useEffect(() => {
    if (open && confirmRef.current) {
      confirmRef.current.focus();
    }
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel?.();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onCancel]);

  if (!open) return null;

  const variants = {
    danger: {
      icon: <Trash2 size={22} className="text-red-500" />,
      iconBg: 'bg-red-100',
      confirmBtn: 'bg-red-600 hover:bg-red-700 focus:ring-red-500 text-white'
    },
    warning: {
      icon: <AlertTriangle size={22} className="text-amber-500" />,
      iconBg: 'bg-amber-100',
      confirmBtn: 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500 text-white'
    },
    info: {
      icon: <Info size={22} className="text-blue-500" />,
      iconBg: 'bg-blue-100',
      confirmBtn: 'bg-pest-blue hover:bg-blue-700 focus:ring-blue-500 text-white'
    },
    question: {
      icon: <HelpCircle size={22} className="text-purple-500" />,
      iconBg: 'bg-purple-100',
      confirmBtn: 'bg-purple-600 hover:bg-purple-700 focus:ring-purple-500 text-white'
    }
  };

  const v = variants[variant] || variants.info;
  const isAlertOnly = cancelLabel === null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCancel} />

      {/* Dialog */}
      <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full animate-in zoom-in-95 duration-150">
        {/* Close button */}
        <button
          onClick={onCancel}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100"
        >
          <X size={16} />
        </button>

        <div className="p-6">
          {/* Icon + Title */}
          <div className="flex items-start space-x-4">
            <div className={`flex-shrink-0 w-10 h-10 rounded-full ${v.iconBg} flex items-center justify-center`}>
              {v.icon}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
              {message && (
                <p className="mt-2 text-sm text-gray-600 leading-relaxed whitespace-pre-line">{message}</p>
              )}
            </div>
          </div>

          {/* Buttons */}
          <div className="mt-6 flex justify-end space-x-3">
            {!isAlertOnly && (
              <button
                onClick={onCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-300"
              >
                {cancelLabel}
              </button>
            )}
            <button
              ref={confirmRef}
              onClick={onConfirm}
              className={`px-4 py-2 text-sm font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-1 ${v.confirmBtn}`}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
